import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  completeOAuthMethodLink,
  completeOAuthSecurityReauth,
  connect,
} from "../api/account";
import { useAuth } from "../auth/useAuth";
import {
  consumeOAuthPkceVerifier,
  consumeOAuthReauthIntent,
  consumeOAuthState,
  rememberOAuthReauthProof,
} from "../auth/tokens";
import {
  errorMessageFor,
  precheckCallback,
  type CallbackPrecheck,
} from "../auth/oauthCallback";

/// Custom URL scheme of the native app, used to hand a desktop sign-in back
/// to it. Per-environment via VITE_APP_SCHEME (prod build sets `meridian`;
/// local dev with the debug app uses `meridian-local`).
const APP_SCHEME = import.meta.env.VITE_APP_SCHEME ?? "meridian";

export default function OAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { markAuthenticated } = useAuth();
  const [apiError, setApiError] = useState("");

  // Precheck in-render: validate required params and CSRF-bind the state
  // against the nonce we put in sessionStorage when we initiated OAuth.
  // `consumeOAuthState` is single-use — capture once via useMemo so a
  // re-render can't accidentally consume a fresh state we haven't set yet.
  const precheck = useMemo<CallbackPrecheck>(
    () => precheckCallback(searchParams, consumeOAuthState),
    [searchParams],
  );
  const pkceVerifier = useMemo(() => {
    if (precheck.kind === "ok") return consumeOAuthPkceVerifier("auth");
    if (precheck.kind === "link") return consumeOAuthPkceVerifier("link");
    if (precheck.kind === "reauth") return consumeOAuthPkceVerifier("reauth");
    return null;
  }, [precheck]);

  useLayoutEffect(() => {
    // OAuth codes and signed state are bearer-like one-time secrets. Remove
    // them before any effect fetches, links render, or same-origin request can
    // include the full callback URL as Referer.
    window.history.replaceState({}, "", "/account/connect/callback");
  }, []);

  useEffect(() => {
    if (precheck.kind === "desktop" || precheck.kind === "connection") {
      // Relay the code into the native app, which exchanges it itself. Both
      // account sign-in and integration-connection use one deep-link path; the
      // app dispatches on the signed `state` (account: vs connection:).
      window.location.href =
        `${APP_SCHEME}://account/connect/callback` +
        `?code=${encodeURIComponent(precheck.code)}&state=${encodeURIComponent(precheck.state)}`;
      return;
    }
    if (precheck.kind !== "ok") return;
    let cancelled = false;
    if (!pkceVerifier) {
      return;
    }

    connect({
      source: precheck.source,
      code: precheck.code,
      state: precheck.state,
      code_verifier: pkceVerifier,
    })
      .then(async (result) => {
        if (cancelled) return;
        if (result.status === "authenticated") {
          await markAuthenticated();
          navigate("/", { replace: true });
        } else if (result.status === "two_factor_required") {
          sessionStorage.setItem("meridian_totp_challenge", result.challenge);
          navigate("/login?two_factor=1", { replace: true });
        } else {
          setApiError("Unexpected response from server. Please try again.");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setApiError(
          err.message || "OAuth authentication failed. Please try again.",
        );
      });

    return () => {
      cancelled = true;
    };
  }, [precheck, pkceVerifier, markAuthenticated, navigate]);

  useEffect(() => {
    if (precheck.kind !== "link") return;
    let cancelled = false;
    if (!pkceVerifier) {
      return;
    }
    completeOAuthMethodLink(precheck.code, precheck.state, pkceVerifier)
      .then(() => {
        if (!cancelled) navigate("/login?method=linked", { replace: true });
      })
      .catch((err: Error) => {
        if (!cancelled) setApiError(err.message || "Could not link that sign-in method.");
      });
    return () => {
      cancelled = true;
    };
  }, [precheck, pkceVerifier, navigate]);

  useEffect(() => {
    if (precheck.kind !== "reauth" || !pkceVerifier) return;
    let cancelled = false;
    completeOAuthSecurityReauth(precheck.code, precheck.state, pkceVerifier)
      .then(({ proof_token }) => {
        if (cancelled) return;
        rememberOAuthReauthProof(proof_token);
        const intent = consumeOAuthReauthIntent();
        navigate(
          intent === "account_deletion"
            ? "/account/data-privacy?reauth=account-deletion"
            : "/account/security?reauth=success",
          { replace: true },
        );
      })
      .catch((err: Error) => {
        if (!cancelled) setApiError(err.message || "Could not verify that provider sign-in.");
      });
    return () => {
      cancelled = true;
    };
  }, [precheck, pkceVerifier, navigate]);

  if (precheck.kind === "desktop" || precheck.kind === "connection") {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-header">
            <h1>Meridian</h1>
            <p>Almost there…</p>
            <p>
              Return to the Meridian app to continue. You can close this tab.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const missingPkceError =
    precheck.kind === "ok" && !pkceVerifier
      ? "This sign-in callback is missing its browser security proof. Start again."
      : precheck.kind === "link" && !pkceVerifier
        ? "This linking callback is missing its browser security proof. Start again."
        : precheck.kind === "reauth" && !pkceVerifier
          ? "This verification callback is missing its browser security proof. Start again."
        : "";
  const errorMessage = errorMessageFor(precheck, apiError || missingPkceError);

  if (errorMessage) {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-header">
            <h1>Meridian</h1>
          </div>
          <div className="error-message">{errorMessage}</div>
          <button
            className="btn btn-primary"
            onClick={() => navigate("/login", { replace: true })}
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <h1>Meridian</h1>
          <p>
            {precheck.kind === "link"
              ? "Linking sign-in method…"
              : precheck.kind === "reauth"
                ? "Verifying provider sign-in…"
                : "Completing sign in…"}
          </p>
        </div>
      </div>
    </div>
  );
}
